import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import {
  useForm,
  FormProvider,
  useFieldArray,
  useWatch,
  useFormContext,
} from "react-hook-form";
import { useXeroClient } from "hooks";
import { useQuery } from "react-query";
import { Page } from "components/common";
import { Attachments } from "components";
import {
  DatePicker,
  FormGroup,
  Input,
  Select,
  SubmitButton,
} from "components/react-hook-form";
import { useTranslation, Trans } from "react-i18next";
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Table,
  UncontrolledButtonDropdown,
} from "reactstrap";
import {
  useAccounts,
  useTaxRates,
  useXeroOrganisationQuery,
} from "queries";
import {
  calculateTax,
  CurrencySymbol,
  formatCurrency,
  isCurrency,
} from "services/util";
import produce from "immer";
import numeral, { options } from "numeral";
import { keyBy, chain, capitalize } from "lodash";
import { NotificationManager } from "react-notifications";
import { Alert } from "reactstrap";
import BackButton from "components/refactoring/BackButton";
import LoadingMsg from "components/common/LoadingMsg";
import InputWithInitialValue from "./InputWIthInitialValue";
import { ManualJournalsView } from "./ManualJournalsView";
import ManualJournalDocument from "components/documents/ManualJournalDocument";
import withDocumentRenderer from "components/DocumentRenderer/withDocumentRenderer";
import HistoryComponent from "components/ShowHistoryComponent";

const createJournalLine = () => {
  return {
    Debit: "",
    Credit: "",
    AccountCode: null,
    Description: "",
    TaxType: null,
    Tracking: []
  };
};

const DownloadCell = withDocumentRenderer({
  onDownload: () => (getDocument, item) =>
    getDocument(
      {
        item,
      },
      `mj_${item.Narration}.pdf`,
    ),
})(({ PrintDocument, DocumentRenderer, DownloadDocument, i, componentDoc, selectedOrganisation, trackingCategories, accounts, organisation, taxRates }) => (
  <>
    <div className="text-end text-left mx-2">
      <DownloadDocument item={i} />
      <DocumentRenderer
        component={componentDoc}
        selectedOrganisation={selectedOrganisation}
        trackingCategories={trackingCategories}
        accounts={accounts}
        organisation={organisation}
        taxRates={taxRates}
      />
    </div>
    <div className="mr-2">
      <PrintDocument label="Print Journal" item={i} is_small={false} />
    </div>
  </>
));

const Totals = ({ taxRates, trackingLength }) => {
  const [lineAmountType, journalLines] = useWatch({
    name: ["LineAmountTypes", "JournalLines"],
  });

  const { setError, clearErrors } = useFormContext();

  const keyedTaxRates = keyBy(taxRates, "TaxType");

  const { subtotalDebit, subtotalCredit, totalDebit, totalCredit, tax } =
    useMemo(() => {
      const result = (journalLines ?? []).reduce(
        (acc, i) => {
          const credit = numeral(i.Credit).value() ?? 0;
          const debit = numeral(i.Debit).value() ?? 0;
          const taxRate = keyedTaxRates[i.TaxType];
          acc.subtotalDebit += debit;
          acc.subtotalCredit += credit;

          if (taxRate) {
            const creditTax = calculateTax(credit, taxRate, lineAmountType);
            const debitTax = calculateTax(debit, taxRate, lineAmountType);

            for (const i of debitTax.components) {
              if (!acc.tax[i.rate]) {
                acc.tax[i.rate] = {
                  debit: 0,
                  credit: 0,
                };
              }
              acc.tax[i.rate].debit += i.tax;
            }

            for (const i of creditTax.components) {
              if (!acc.tax[i.rate]) {
                acc.tax[i.rate] = {
                  debit: 0,
                  credit: 0,
                };
              }
              acc.tax[i.rate].credit += i.tax;
            }

            acc.totalCredit += creditTax.total;
            acc.totalDebit += debitTax.total;
          } else {
            acc.totalDebit += debit;
            acc.totalCredit += credit;
          }

          return acc;
        },
        {
          subtotalCredit: 0,
          subtotalDebit: 0,
          totalCredit: 0,
          totalDebit: 0,
          tax: {},
        }
      );

      result.tax = chain(result.tax)
        .reduce((acc, i, rate) => {
          acc.push({
            rate,
            ...i,
          });
          return acc;
        }, [])
        .sortBy("rate")
        .value();

      return result;
    }, [journalLines, lineAmountType]);

  const diff = totalDebit.toFixed(2) - totalCredit.toFixed(2);
  const isDiff = !!diff;

  useEffect(() => {
    if (isDiff) {
      setError("diff", {
        type: "diff",
      });

      return () => clearErrors("diff");
    }
  }, [isDiff]);

  return (
    <div className="row flex-row-reverse text-right mx-3">
      <div className="col-6">
        <div className="row py-2 border-bottom">
          <div className="col-6">
            <Trans>Subtotal</Trans>
          </div>
          <div className="col-3">
            {formatCurrency(subtotalDebit)}
          </div>
          <div className="col-3">
            {formatCurrency(subtotalCredit)}
          </div>
        </div>
        {tax.map((i) => (
          <div className="row py-2 border-bottom" key={i.rate}>
            <div className="col-6">
              {lineAmountType === "Inclusive" ? (
                <Trans>
                  Includes Tax at {{ percent: numeral(i.rate).format("0.00%") }}
                </Trans>
              ) : null}
              {lineAmountType === "Exclusive" ? (
                <Trans>
                  Total Tax at {{ percent: numeral(i.rate).format("0.00%") }}
                </Trans>
              ) : null}
            </div>
            <div className="col-3">
              {formatCurrency(i.debit)}
            </div>
            <div className="col-3">
              {formatCurrency(i.credit)}
            </div>
          </div>
        ))}
        <div className="row py-2 border-bottom">
          <div className="col-6">
            <Trans>TOTAL</Trans>
          </div>
          <div className="col-3">
            <strong>{formatCurrency(totalDebit)}</strong>
          </div>
          <div className="col-3">
            <strong>{formatCurrency(totalCredit)}</strong>
          </div>
        </div>
        {diff ? (
          <div className="row py-2 border-bottom">
            <div className="col-6">
              <Trans>Total is out by:</Trans>
            </div>
            <div className="col-6">
              {formatCurrency(Math.abs(diff))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export const ManualJournalsEdit = ({ selectedOrganisation }) => {
  const methods = useForm({
    mode: "onBlur",
  });
  const [error, setError] = useState();
  const { copy } = useLocation().state ?? {};
  const { id } = useParams();
  const isNew = id === "new";
  const [isViewMode, setView] = useState(!isNew);
  const { reset, control, setValue, handleSubmit, watch, getValues } = methods;
  const narration = watch('Narration', "")
  const xero = useXeroClient();
  const { t } = useTranslation();
  const history = useHistory();

  useEffect(() => {
    setView(!isNew)
  }, [id])


  const lineAmountTypes = useMemo(
    () => [
      {
        label: t("Tax Inclusive"),
        value: "Inclusive",
      },
      {
        label: t("Tax Exclusive"),
        value: "Exclusive",
      },
      {
        label: t("No Tax"),
        value: "NoTax",
      },
    ],
    [t]
  );

  const { data: trackingCategories } = useQuery(
    [
      "trackingcategories",
      {
        params: {
          Status: "ACTIVE",
        },
      },
    ],
    ({ queryKey }) => xero.get(...queryKey).get("data"),
    {
      placeholderData: [],
    }
  );

  const { data: organisation } = useXeroOrganisationQuery();
  const { data: taxRates } = useTaxRates({
    placeholderData: [],
  });

  const { data: accounts } = useAccounts({
    placeholderData: [],
    select: (v) =>
      v.filter((i) => i.Code && (!i.SystemAccount || i.SystemAccount === 'RETAINEDEARNINGS') && i.Type !== "BANK"),
  });

  const handleLineAmountChange = useCallback(
    (primaryName, secondaryName) => (e) => {
      const { value } = e.target;
      const options = {
        shouldDirty: true,
        shouldValidate: true,
      };

      if (value) {
        setValue(secondaryName, "", options);
      }
      setValue(primaryName, value, options);
    },
    [setValue]
  );

  const { refetch } = useQuery(
    ["manualjournals", { id }],
    async () => {
      if (isNew) {
        return (
          copy ?? {
            Narration: "",
            JournalLines: [...Array(2)].map((item, i) => createJournalLine()),
            Date: null,
            Status: "DRAFT",
            LineAmountTypes: "NoTax",
            ShowOnCashBasisReports: true,
            Attachments: [],
          }
        );
      }
      let values = null, attachments = null, history = null
      try {
        [values, attachments, history] = await Promise.all([
          xero.get(`manualjournals/${id}`).get("data"),
          xero.get(`manualjournals/${id}/attachments`).get("data"),
          xero.get(`manualjournals/${id}/History`).get("data"),
        ]);
      } catch (err) {
        console.log('error', err)
      }

      return {
        ...values,
        Attachments: attachments,
        History: history,
      };
    },
    {
      onSuccess: reset,
      select: produce((draft) => {
        try {
          for (const i of draft.JournalLines) {
            i.Credit = "";
            i.Debit = "";

            if (i.LineAmount < 0) {
              i.Credit = i.LineAmount * -1;
            } else if (i.LineAmount > 0) {
              i.Debit = i.LineAmount;
            }
          }
        }
        catch (err) {
          return
        }
      }),
    }
  );

  const { fields, remove, append } = useFieldArray({
    control,
    name: "JournalLines",
    keyName: "key",
  });

  const status = watch("Status");
  const lineAmountType = watch("LineAmountTypes");
  const mjHistory = watch("History");
  const attachments = watch("Attachments");
  const isNoTax = lineAmountType === "NoTax";
  const isDraft = status === "DRAFT"

  useEffect(() => {
    if (isNoTax) {
      const journalLines = getValues("JournalLines");

      setValue(
        "JournalLines",
        journalLines.map((i) => ({
          ...i,
          TaxType: "NONE",
        }))
      );
    }
  }, [isNoTax]);

  async function onCopy(x) {
    console.log(x, "x - OnCopy")

    const copy = {
      JournalLines: fields,
      ManualJournalID: undefined,
      Status: "DRAFT",
      Date: null,
      Attachments: [],
    }


    history.push("new", {
      copy: {
        Narration: x.Narration,
        JournalLines: fields,
        ManualJournalID: undefined,
        Status: "DRAFT",
        Date: null,
        Attachments: [],
      },
    });
    await refetch();
  }

  async function onReverse(x) {
    history.replace("new", {
      copy: {
        ...x,
        ManualJournalID: undefined,
        Narration: `${t("Reversal of:")} ${x.Narration}`,
        JournalLines: x.JournalLines.map((i) => ({
          ...i,
          LineAmount: i.LineAmount * -1,
        })),
        Status: "DRAFT",
        Date: null,
        Attachments: [],
      },
    });
    await refetch();
  }

  async function onVoid({ ManualJournalID }) {
    try {
      await xero.patch(`manualjournals/${ManualJournalID}`, {
        ManualJournalID,
        Status: "VOIDED",
      });
      NotificationManager.success(t("Void successful"));
      await refetch();
    } catch (ex) {
      NotificationManager.error(t("Failed to void"));
    }
  }

  async function onDelete({ ManualJournalID }) {
    try {
      await xero.patch(`manualjournals/${ManualJournalID}`, {
        ManualJournalID,
        Status: "DELETED",
      });
      NotificationManager.success(t("Deleted Successfully"));
      await refetch();
    } catch (ex) {
      NotificationManager.error(t("Failed to delete"));
    }
  }

  const onSubmit =
    ({ addAnother, draft, redirectDraft, redirectPosted } = {}) => {
      return async (x) => {
        setError(null);
        const { ManualJournalID } = x;
        const payload = produce(x, (d) => {
          for (const i of d.JournalLines) {
            const debit = numeral(i.Debit).value();
            const credit = numeral(i.Credit).value();
            i.LineAmount = 0;

            if (debit !== null) {
              i.LineAmount = debit;
            } else if (credit !== null) {
              i.LineAmount = credit * -1;
            }
            delete i.IsBlank;
            delete i.Debit;
            delete i.Credit;
          }

          d.Status = draft ? "DRAFT" : "POSTED";
        });

        try {
          const resp = await (ManualJournalID
            ? xero.patch(`manualjournals/${ManualJournalID}`, payload)
            : xero.post("manualjournals", payload)
          ).get("data");

          const successMessage =
            ManualJournalID
              ? t("Updated successfully")
              : draft
                ? t("Saved as Draft successfully")
                : t("Posted successfully");

          NotificationManager.success(successMessage);

          if (addAnother) {
            history.push("new");
          } else if (!ManualJournalID && redirectDraft) {
            setTimeout(() => {
              history.push("/accounts/manual_journals/draft");
            }, 600);
          } else if (redirectPosted) {
            setTimeout(() => {
              history.push("/accounts/manual_journals/posted");
            }, 600);
          } else if (!ManualJournalID) {
            history.push(resp.ManualJournalID);
          }
          await refetch();
        } catch (ex) {
          const { code, detail } = ex?.response?.data ?? {};

          if (code === "ValidationError") {
            setError(detail);
          }
        }
      };
    }

  const data = getValues()

  return (
    <Page title={isNew ? "New Manual Journal" : "Manual Journal"}>
      <BackButton />
      {!isNew ? (
        <div className="d-flex justify-content-end mb-1">
          <Attachments
            className="mx-0"
            xero={xero}
            hideIncludeOnline
            onAttachmentsChanged={(attachments) => {
              setValue("Attachments", [
                ...getValues("Attachments"),
                ...attachments,
              ]);
            }}
            baseUrl={`manualjournals/${id}`}
            attachments={attachments ?? []}
          />
          <div className="mx-1" />
          <UncontrolledButtonDropdown>
            <DropdownToggle caret color="primary" size="sm">
              <Trans>Journal Options</Trans>
            </DropdownToggle>
            <DropdownMenu>
              {status === "POSTED" ? (
                <DropdownItem onClick={handleSubmit(onVoid)}>
                  <Trans>Void</Trans>
                </DropdownItem>
              ) : null}
              {isDraft ? (
                <DropdownItem onClick={handleSubmit(onDelete)}>
                  <Trans>Delete</Trans>
                </DropdownItem>
              ) : null}
              {isDraft || status === "POSTED" ? (
                <DropdownItem onClick={handleSubmit(onReverse)}>
                  <Trans>Reverse</Trans>
                </DropdownItem>
              ) : null}
              <DropdownItem onClick={handleSubmit(onCopy)}>
                <Trans>Copy</Trans>
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledButtonDropdown>
          {!isNew &&
            <>
              <DownloadCell
                i={data}
                componentDoc={ManualJournalDocument}
                selectedOrganisation={selectedOrganisation}
                trackingCategories={trackingCategories}
                accounts={accounts}
                organisation={organisation}
                taxRates={taxRates}
              />
              <Button size="sm" className="ml-0" color="primary" onClick={() => setView(prev => !prev)}><Trans>{isViewMode ? "Edit" : "View"}</Trans></Button>
            </>
          }
        </div>
      ) : null}
      {organisation && taxRates && trackingCategories && methods ?
        <FormProvider {...methods}>
          {isViewMode ?
            <ManualJournalsView
              data={data}
              taxRates={taxRates}
              accounts={accounts}
              trackingCategories={trackingCategories}
              organisation={organisation}
              fields={fields}
              lineAmountType={lineAmountType}
              journalLines={data.JournalLines}
              t={t} />
            :
            <>
              <p className="tw--mt-4 tw-font-medium tw-text-sm">
                {t(capitalize(status))}
              </p>
              <FormGroup label={t("Narration")} name="Narration" required className="pb-2 pt-2">
                <Input
                  className="form-control" style={{ backgroundColor: '#F4F7FE' }}
                  name="Narration"
                  component="textarea"
                  rows={2}
                  rules={{ required: true }}
                />
              </FormGroup>
              <FormGroup label={t("Date")} name="Date" required className="pb-2 pt-2">
                <DatePicker
                  className="form-control"
                  name="Date"
                  rules={{ required: true }} style={{ backgroundColor: '#F4F7FE' }}
                />
              </FormGroup>
              <FormGroup className="pb-2 pt-2"
                check
                name="ShowOnCashBasisReports"
                htmlFor="ShowOnCashBasisReports"
                label={t("Show journal on cash basis reports")}
              >
                <Input
                  type="checkbox"
                  name="ShowOnCashBasisReports"
                  className="form-check-input"
                />
              </FormGroup>
              <hr />
              <div className="tw-flex tw-justify-end">
                <FormGroup
                  label={t("Amounts are")}
                  className="tw-w-48 pb-2 pt-2"
                  name="LineAmountTypes"
                >
                  <Select style={{ width: 200, backgroundColor: '#F4F7FE' }}
                    rules={{ required: true }}
                    clearable={false}
                    options={lineAmountTypes}
                    simpleValue
                    name="LineAmountTypes"
                  />
                </FormGroup>
              </div>
              <Table size="sm" className="small">
                <thead>
                  <tr>
                    <th className="text-nowrap">
                      <Trans>Description</Trans>
                    </th>
                    <th className="text-nowrap">
                      <Trans>Account</Trans>
                    </th>
                    <th className="text-nowrap">
                      <Trans>Tax Rate</Trans>
                    </th>
                    {trackingCategories?.map((i) => (
                      <th key={i.TrackingCategoryID}><Trans>{i.Name}</Trans></th>
                    ))}
                    <th className="text-nowrap">
                      {t("Debit")} <Trans><CurrencySymbol currency={organisation?.BaseCurrency}/></Trans>
                    </th>
                    <th className="text-nowrap">
                      {t("Credit")} <Trans><CurrencySymbol currency={organisation?.BaseCurrency}/></Trans>
                    </th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {fields?.map((i, idx) => {
                    const n = (v) => `JournalLines.${idx}.${v}`;
                    return (
                      <tr key={i.key}>
                        <td>
                          <FormGroup name={n("Description")} className="mb-0" >
                            <InputWithInitialValue
                              rules={{ required: true }}
                              className="form-control form-control-sm"
                              name={n("Description")}
                              defaultValue={i.Description}
                              style={{ width: 200, backgroundColor: '#F4F7FE' }}
                              initialValue={narration || ''}
                              setV={setValue}
                            />
                          </FormGroup>
                        </td>
                        <td>
                          <FormGroup
                            className="tw-min-w-[15rem] mb-0"
                            name={n("AccountCode")}
                          >
                            <Select style={{ width: 120, backgroundColor: '#F4F7FE' }}
                              onChange={(v) => {
                                if (v) {
                                  setValue(
                                    n("TaxType"),
                                    isNoTax ? "NONE" : v.TaxType
                                  );
                                }
                                setValue(n("AccountCode"), v?.Code ?? null);
                              }}
                              name={n("AccountCode")}
                              defaultValue={i.AccountCode}
                              rules={{
                                required: true,
                              }}
                              options={accounts}
                              labelKey="Name"
                              valueKey="Code"
                            />
                          </FormGroup>
                        </td>
                        <td>
                          <FormGroup
                            className="tw-min-w-[12rem] mb-0"
                            name={n("TaxType")}
                          >
                            <Select style={{ width: 100, backgroundColor: '#F4F7FE' }}
                              disabled={isNoTax}
                              name={n("TaxType")}
                              simpleValue
                              rules={{
                                required: true,
                              }}
                              options={taxRates}
                              labelKey="Name"
                              valueKey="TaxType"
                            />
                          </FormGroup>
                        </td>
                        {trackingCategories.map((tracking, index) => (
                          <td key={tracking.TrackingCategoryID}>
                            <FormGroup
                              className="tw-min-w-[12rem] mb-0"
                              name={n("Tracking")}
                            >
                              <Select style={{ width: 120, backgroundColor: '#F4F7FE' }}
                                name={n(`Tracking.${index}.Option`)}
                                onChange={(v) => {
                                  setValue(n(`Tracking.${index}`), v ? { Name: tracking.Name, Option: v.Name, TrackingCategoryID: tracking.TrackingCategoryID, TrackingOptionID: v.TrackingOptionID } : null);
                                }}
                                options={trackingCategories?.[index]?.Options}
                                labelKey="Name"
                                valueKey="Name"
                                clearable={false}
                              />
                            </FormGroup>
                          </td>
                        ))}
                        <td>
                          <FormGroup name={n("Debit")} className="mb-0">
                            <Input style={{ backgroundColor: '#F4F7FE' }}
                              rules={{
                                validate: (v) => !v || isCurrency(v),
                              }}
                              name={n("Debit")}
                              className="form-control form-control-sm border"
                              onBlur={handleLineAmountChange(n("Debit"), n("Credit"))}
                            />
                          </FormGroup>
                        </td>
                        <td>
                          <FormGroup name={n("Credit")} className="mb-0">
                            <Input style={{ backgroundColor: '#F4F7FE' }}
                              rules={{
                                validate: (v) => !v || isCurrency(v),
                              }}
                              name={n("Credit")}
                              className="form-control form-control-sm border"
                              onBlur={handleLineAmountChange(n("Credit"), n("Debit"))}
                            />
                          </FormGroup>
                        </td>
                        <td className="tw-w-1">
                          <Button color="link" size="sm" onClick={() => remove(idx)}>
                            <span className="fa fa-times text-danger" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </>}
          {data.ManualJournalID &&
            <>
              <Totals taxRates={taxRates} trackingLength={trackingCategories?.length} />
              {isViewMode && <HistoryComponent historyItem={mjHistory?.HistoryRecords} type="Manual Journal" t={t} />}
            </>
          }
          {!isViewMode && <div>
            {isDraft &&
              <Button
                color="success"
                className="mb-4 btn-success"
                onClick={() => append(createJournalLine())}
              >
                <Trans>Add a new line</Trans>
              </Button>}
            <div className="tw-flex">
              {isDraft ? (
                <UncontrolledButtonDropdown>
                  <SubmitButton
                    color="primary"
                    onClick={handleSubmit(onSubmit({ draft: true, redirectDraft: true }))}
                  >
                    <Trans>Save as draft</Trans>
                  </SubmitButton>
                  <DropdownToggle caret color="primary" />
                  <DropdownMenu>
                    <DropdownItem onClick={handleSubmit(onSubmit({ draft: true, redirectDraft: true }))}>
                      <Trans>Save as draft</Trans>
                    </DropdownItem>
                    <DropdownItem
                      onClick={handleSubmit(
                        onSubmit({ addAnother: true, draft: true })
                      )}
                    >
                      <Trans>Save & add another</Trans>
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledButtonDropdown>
              ) : null}
              <div className="tw-mx-auto" />
              {isDraft ? (
                <UncontrolledButtonDropdown>
                  <SubmitButton color="success" onClick={handleSubmit(onSubmit({ redirectPosted: true }))}>
                    <Trans>Post</Trans>
                  </SubmitButton>
                  <DropdownToggle caret color="success" />
                  <DropdownMenu>
                    <DropdownItem onClick={handleSubmit(onSubmit({ redirectPosted: true }))}>
                      <Trans>Post</Trans>
                    </DropdownItem>
                    <DropdownItem
                      onClick={handleSubmit(onSubmit({ addAnother: true }))}
                    >
                      <Trans>Post & add another</Trans>
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledButtonDropdown>
              ) : null}
              {!isDraft ? (
                <SubmitButton color="success" onClick={handleSubmit(onSubmit({ redirectPosted: true }))}>
                  <Trans>Update</Trans>
                </SubmitButton>
              ) : null}
            </div>
            {error ? (
              <Alert color="danger" className="mt-4">
                <ul className="mb-0">
                  {error.map((i, idx) => (
                    <li key={idx}>
                      <Trans>{i}</Trans>
                    </li>
                  ))}
                </ul>
              </Alert>
            ) : null}
          </div>
          }
        </FormProvider> : <LoadingMsg />
      }

    </Page>
  );
};
